import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { analysisService } from '../../services/analysisService';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faChevronRight, faFolderOpen, faFilePdf, faFileWord, faTrash, faFileDownload, faFileExport, faBook } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { useState } from 'react';

export default function DocumentList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Helper function to get icon and color based on document type
  const getDocumentIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'PDF':
        return { icon: faFilePdf, bgColor: 'bg-red-100', iconColor: 'text-red-600' };
      case 'DOCX':
      case 'DOC':
        return { icon: faFileWord, bgColor: 'bg-blue-100', iconColor: 'text-blue-600' };
      case 'TXT':
        return { icon: faFileAlt, bgColor: 'bg-gray-100', iconColor: 'text-gray-600' };
      case 'TSV':
        return { icon: faFileAlt, bgColor: 'bg-green-100', iconColor: 'text-green-600' };
      default:
        return { icon: faFileAlt, bgColor: 'bg-gray-100', iconColor: 'text-gray-600' };
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['grouped-analyses', 'DOCUMENT'],
    queryFn: () => analysisService.getGroupedAnalyses('DOCUMENT'),
  });

  const deleteMutation = useMutation({
    mutationFn: (analysisId: number) => analysisService.deleteAnalysis(analysisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grouped-analyses', 'DOCUMENT'] });
      toast.success('Documento eliminado correctamente');
      setDeletingId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al eliminar el documento');
      setDeletingId(null);
    },
  });

  const handleDelete = (e: React.MouseEvent, analysisId: number, title: string) => {
    e.stopPropagation(); // Prevent navigation

    if (window.confirm(`¿Estás seguro de que quieres eliminar "${title}" y todas sus secciones?`)) {
      setDeletingId(analysisId);
      deleteMutation.mutate(analysisId);
    }
  };

  const handleDownloadReport = async (e: React.MouseEvent, analysisId: number, title: string) => {
    e.stopPropagation(); // Prevent navigation

    try {
      toast.loading('Generando informe PDF...', { id: 'pdf-generation' });

      const blob = await analysisService.downloadComplianceReport(analysisId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Informe_UNE_${title.replace(/[^a-zA-Z0-9]/g, '_')}_${analysisId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Informe PDF descargado correctamente', { id: 'pdf-generation' });
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      toast.error(error.response?.data?.message || 'Error al generar el informe PDF', { id: 'pdf-generation' });
    }
  };

  const handleDownloadGlossaryTerms = async (e: React.MouseEvent, analysisId: number, title: string) => {
    e.stopPropagation(); // Prevent navigation

    try {
      toast.loading('Exportando términos de glosario...', { id: 'glossary-export' });

      const blob = await analysisService.downloadGlossaryTerms(analysisId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Terminos_Glosario_${title.replace(/[^a-zA-Z0-9]/g, '_')}_${analysisId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Términos de glosario exportados correctamente', { id: 'glossary-export' });
    } catch (error: any) {
      console.error('Error downloading glossary terms:', error);
      toast.error(error.response?.data?.message || 'Error al exportar términos de glosario', { id: 'glossary-export' });
    }
  };

  const handleExportSections = async (e: React.MouseEvent, analysisId: number, title: string) => {
    e.stopPropagation(); // Prevent navigation

    try {
      toast.loading('Generando archivo Excel...', { id: 'sections-export' });

      const blob = await analysisService.downloadSectionAnalysis(analysisId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Analisis_Secciones_${title.replace(/[^a-zA-Z0-9]/g, '_')}_${analysisId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Archivo Excel descargado correctamente', { id: 'sections-export' });
    } catch (error: any) {
      console.error('Error downloading section analysis:', error);
      toast.error(error.response?.data?.message || 'Error al generar archivo Excel', { id: 'sections-export' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando documentos...</p>
        </div>
      </div>
    );
  }

  if (!data || data.analyses.length === 0) {
    return (
      <div className="text-center py-12">
        <FontAwesomeIcon icon={faFolderOpen} className="text-6xl text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg">No hay documentos analizados</p>
        <p className="text-gray-400 text-sm mt-2">
          Sube un documento (PDF, Word, TXT, TSV) para comenzar
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-700">
          Documentos Analizados ({data.total})
        </h3>
        <div className="grid gap-4">
          {data.analyses.map((doc) => {
            const { icon, bgColor, iconColor } = getDocumentIcon(doc.sourceType);
            return (
              <Card
                key={doc.analysisId}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/analysis/document/${doc.analysisId}/sections`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`${bgColor} p-3 rounded-lg`}>
                        <FontAwesomeIcon icon={icon} className={`${iconColor} text-2xl`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900">{doc.title}</h4>
                          <Badge className={`flex items-center gap-1.5 ${
                            doc.auditStatus === 'AUDITED'
                              ? 'bg-green-500/90 text-white border-green-400'
                              : 'bg-gray-400/90 text-white border-gray-300'
                          }`}>
                            <span className={`w-2 h-2 rounded-full ${
                              doc.auditStatus === 'AUDITED' ? 'bg-green-200' : 'bg-gray-200'
                            }`}></span>
                            {doc.auditStatus === 'AUDITED' ? 'Auditado' : 'Sin Auditar'}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">
                            {doc.sourceType}
                          </span>
                          <span>•</span>
                          <span>{doc.sectionCount} secciones</span>
                          <span>•</span>
                          <span>{new Date(doc.createdAt).toLocaleDateString('es-ES')}</span>
                          {doc.sector && (
                            <>
                              <span>•</span>
                              <span className="flex items-center">
                                {doc.sector.icon} {doc.sector.name}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Lenguaje Claro Compliance Summary */}
                        {doc.analysisMetadata?.b2Criteria && (() => {
                          const b2Score = doc.analysisMetadata.b2Criteria.b2Score;

                          // Calcular mejora potencial con glosario
                          const glossaryCriterion = doc.analysisMetadata.b2Criteria.criteria.find(
                            (c: any) => c.name === 'Sin términos que requieran glosario'
                          );
                          let potentialImprovementPoints = 0;
                          if (glossaryCriterion && doc.analysisMetadata?.glossaryTerms?.count > 0) {
                            const currentContribution = glossaryCriterion.status === 'pass'
                              ? glossaryCriterion.weight
                              : glossaryCriterion.status === 'warning'
                              ? glossaryCriterion.weight * 0.5
                              : 0;
                            potentialImprovementPoints = glossaryCriterion.weight - currentContribution;
                          }

                          // Puntuación con glosario incluido
                          const potentialAutomaticScore = Math.min(b2Score + potentialImprovementPoints, 80);
                          const potentialAutomaticPercentage = (potentialAutomaticScore / 80) * 100;
                          const passesAutomatic = potentialAutomaticPercentage >= 85;

                          // Puntuación global con manuales incluidos
                          const potentialGlobalScore = potentialAutomaticScore + 20; // Asumiendo todos los manuales correctos
                          const passesGlobal = potentialGlobalScore >= 85;

                          return (
                            <div className="mt-2 space-y-1">
                              {/* Automatic Criteria Badge */}
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-medium text-gray-600">Criterios Automáticos:</span>
                                <span className={`px-2 py-0.5 rounded-full font-semibold ${
                                  passesAutomatic
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {passesAutomatic ? '✓ Cumple' : '⚠ No cumple'} ({potentialAutomaticPercentage.toFixed(1)}% con glosario)
                                </span>
                              </div>

                              {/* Global Adequacy Badge */}
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-medium text-gray-600">Adecuación Global:</span>
                                <span className={`px-2 py-0.5 rounded-full font-semibold ${
                                  passesGlobal
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {passesGlobal ? '✓ Puede alcanzar 85' : '✗ No puede alcanzar 85'} ({potentialGlobalScore.toFixed(1)}/100 pts con manuales)
                                </span>
                              </div>
                            </div>
                          );
                        })()}

                      {/* Document-Level Compliance Summary */}
                      {doc.complianceStats && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <h5 className="text-xs font-semibold text-gray-700 mb-2">Cumplimiento del Documento Completo</h5>
                          <div className="flex items-center gap-4">
                            {/* Lenguaje Claro Compliance */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">Lenguaje Claro:</span>
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                doc.complianceStats.lenguajeClaro.complies
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {doc.complianceStats.lenguajeClaro.complies ? '✓ Cumple' : '✗ No cumple'}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({doc.complianceStats.lenguajeClaro.percentage.toFixed(1)}% de textos)
                              </span>
                            </div>

                            {/* Comprensibilidad B2 Compliance */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">Comprensibilidad B2:</span>
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                doc.complianceStats.comprensibilidad.complies
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {doc.complianceStats.comprensibilidad.complies ? '✓ PASA' : '✗ NO PASA'}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({doc.complianceStats.comprensibilidad.percentage.toFixed(1)}% de textos)
                              </span>
                            </div>
                          </div>

                          {/* Export Buttons Grid */}
                          <div className="mt-3 grid grid-cols-3 gap-2">
                            {/* Download Report Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleDownloadReport(e, doc.analysisId, doc.title)}
                              className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                            >
                              <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                              Informe UNE
                            </Button>

                            {/* Download Glossary Terms Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleDownloadGlossaryTerms(e, doc.analysisId, doc.title)}
                              className="w-full text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                            >
                              <FontAwesomeIcon icon={faBook} className="mr-2" />
                              Términos Glosario
                            </Button>

                            {/* Export Section Analysis Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => handleExportSections(e, doc.analysisId, doc.title)}
                              className="w-full text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                            >
                              <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                              Análisis Excel
                            </Button>
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleDelete(e, doc.analysisId, doc.title)}
                        disabled={deletingId === doc.analysisId}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          className={deletingId === doc.analysisId ? 'animate-pulse' : ''}
                        />
                      </Button>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="text-gray-400 text-xl"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </>
  );
}
